/**
 * AppsFlyer Smart Banner Integration
 *
 * Integrates AppsFlyer Smart Banner v2 SDK for displaying mobile app download banners.
 * The banner is only shown to premium users (plan 2) and includes comprehensive
 * event tracking for user interactions.
 *
 * @see https://dev.appsflyer.com/hc/docs/dl_smart_banner_v2 - Official AppsFlyer Smart Banner v2 documentation
 */

import useCurrentUser from "@/hooks/useCurrentUser";
import useGTM from "@/hooks/useGTM";
import Script from "next/script";
import posthog from "posthog-js";
import { useEffect, useRef } from "react";

/**
 * Component to manage AppsFlyer SDK and banner display
 * The script loads for all users but banner display is controlled by user plan status.
 *
 * @returns JSX element containing the AppsFlyer script
 */
export function AppsFlyerScript() {
  const { currentUser, isLoggedIn, isFetching } = useCurrentUser();
  const { pushEvent } = useGTM();

  /**
   * Ref to track if banner has been initialized to prevent duplicate calls
   * This prevents weird gaps and multiple banner elements
   */
  const bannerInitializedRef = useRef(false);

  useEffect(() => {
    // Only show banners for premium users (plan 2)
    const isPremiumUser = isLoggedIn && currentUser?.plan === 2;

    // Don't initialize if already done or still fetching user data
    if (bannerInitializedRef.current || isFetching) {
      return;
    }

    /**
     * Check if AppsFlyer SDK is available and initialize banner
     * Retries every 100ms until SDK is loaded
     */
    const checkAppsFlyer = () => {
      if (window.AF) {
        // Only show banner for premium users
        if (isPremiumUser) {
          // Mark as initialized to prevent duplicate calls
          bannerInitializedRef.current = true;

          // Show the banner using AppsFlyer SDK
          window.AF("banners", "showBanner");

          // Track banner shown event
          posthog.capture("appsflyer_banner_shown");
          pushEvent("appsflyer_banner_shown", {});

          // Set up event monitoring for user interactions
          setupBannerEventMonitoring();
        }
      } else {
        // Retry after a short delay if SDK not yet loaded
        setTimeout(checkAppsFlyer, 100);
      }
    };

    checkAppsFlyer();
  }, [currentUser, isLoggedIn, isFetching]);

  /**
   * Set up event monitoring for AppsFlyer banner interactions
   *
   * The AppsFlyer SDK doesn't provide callbacks, so we monitor DOM changes
   * and click events manually to track user interactions.
   */
  const setupBannerEventMonitoring = () => {
    /**
     * Handle specific button clicks within the AppsFlyer banner
     * Tracks CTA (download) button clicks and close button clicks
     *
     * @param event - Mouse click event
     */
    const handleSpecificButtonClicks = (event: MouseEvent) => {
      const target = event.target as Element;

      // Check for CTA button clicks (Download button)
      if (target.closest("[data-af-cta-button]")) {
        const ctaButton = target.closest("[data-af-cta-button]") as HTMLElement;
        const ctaUrl = ctaButton.getAttribute("data-af-cta-url");
        const buttonText = ctaButton.textContent?.trim();

        // Track CTA button click in PostHog
        posthog.capture("appsflyer_banner_cta_clicked", {
          button_text: buttonText,
          cta_url: ctaUrl,
        });

        // Track CTA button click in GTM
        pushEvent("appsflyer_banner_cta_clicked", {
          button_text: buttonText,
          cta_url: ctaUrl,
        });
      }

      // Check for close button clicks
      if (target.closest("[data-af-close-button]")) {
        // Track close button click in PostHog
        posthog.capture("appsflyer_banner_dismissed");

        // Track close button click in GTM
        pushEvent("appsflyer_banner_dismissed", {});
      }
    };

    /**
     * Monitor banner visibility changes by observing DOM mutations
     * Tracks when banner is dismissed by style changes. hacky, but works.
     */
    const setupVisibilityMonitoring = () => {
      // Check if banner element exists in DOM (injected by AppsFlyer SDK)
      const banner = document.getElementById("smart-banner");

      if (banner) {
        /**
         * Observe the banner for changes to style attributes
         * This detects when banner is hidden/dismissed
         */
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (
              mutation.type === "attributes" &&
              mutation.attributeName === "style"
            ) {
              const banner = document.getElementById("smart-banner");
              if (banner) {
                const isVisible =
                  banner.style.display !== "none" &&
                  banner.style.visibility !== "hidden" &&
                  banner.offsetParent !== null;

                // Track banner dismissed when it becomes hidden
                if (!isVisible) {
                  posthog.capture("appsflyer_banner_dismissed");
                  pushEvent("appsflyer_banner_dismissed", {});
                }
              }
            }
          });
        });

        observer.observe(banner, {
          attributes: true,
          attributeFilter: ["style", "class"],
        });
      } else {
        /**
         * Monitor for banner creation if it doesn't exist yet
         * AppsFlyer injects the banner asynchronously
         */
        const bannerObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              if (
                node.nodeType === 1 &&
                (node as Element).id === "smart-banner"
              ) {
                setupVisibilityMonitoring();
              }
            });
          });
        });

        bannerObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });
      }
    };

    // Add event listeners for click tracking
    document.addEventListener("click", handleSpecificButtonClicks);

    // Set up visibility monitoring
    setupVisibilityMonitoring();
  };

  /**
   * Render the AppsFlyer Smart Banner v2 SDK script
   *
   * The script is always rendered but banner display is controlled by the logic above.
   * Uses the 'afterInteractive' strategy to load after page is interactive.
   *
   * @see https://dev.appsflyer.com/hc/docs/dl_smart_banner_v2#installation
   */
  return (
    <Script
      id="appsflyer-banners"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `
          !function(t,e,n,s,a,c,i,o,p){t.AppsFlyerSdkObject=a,t.AF=t.AF||function(){(t.AF.q=t.AF.q||[]).push([Date.now()].concat(Array.prototype.slice.call(arguments)))},t.AF.id=t.AF.id||i,t.AF.plugins={},o=e.createElement(n),p=e.getElementsByTagName(n)[0],o.async=1,o.src="https://websdk.appsflyer.com?"+(c.length>0?"st="+c.split(",").sort().join(",")+"&":"")+(i.length>0?"af_id="+i:""),p.parentNode.insertBefore(o,p)}(window,document,"script",0,"AF","banners",{banners: {key: "5198ec2c-7696-4ed8-bf1b-62b8e659848a"}});
        `,
      }}
    />
  );
}
